<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'

import { useAlertSummary } from '../composables/useAlertSummary'
import { useAlertWizard } from '../composables/useAlertWizard'
import type { AlertRule } from '../models/alert.types'
import type { ChannelAvailability } from '../models/channel.types'
import { alertOrpheusBrief } from '../service/alertOrpheusBrief'
import {
  AlertValidationError,
  type AlertStepId,
  type AlertValidationKey,
} from '../service/alertValidator'

import AlertSummaryPanel from './AlertSummaryPanel.vue'
import AlertWizardOrpheusSwitch from './AlertWizardOrpheusSwitch.vue'
import StepConditions from './StepConditions.vue'
import StepFilter from './StepFilter.vue'
import StepMonitor from './StepMonitor.vue'
import StepNotify from './StepNotify.vue'
import StepReview from './StepReview.vue'

import { useI18n } from '@/shared/i18n'
import { Alert, Button, Modal, useToast } from '@/shared/ui'

const props = withDefaults(
  defineProps<{
    rule?: AlertRule | null
    readOnly?: boolean
    manageDisabled?: boolean
    workspaceId?: number | string | undefined
    notificationTypeId?: number | null | undefined
    channels?: readonly ChannelAvailability[]
    /** An Orpheus card's draft (`form_values`): over `rule` it fills an edit, alone a new alert. */
    formValues?: Record<string, unknown> | null
    /** The Orpheus setup panel can open from here (v3 "Create with Orpheus", ORP-22826). */
    canSwitchToOrpheus?: boolean
    /** Why the form opened instead of the Orpheus panel, in one sentence. */
    notice?: string
    quotaUsed?: number
    quotaLimit?: number | null
  }>(),
  {
    rule: null,
    readOnly: false,
    manageDisabled: false,
    channels: () => [],
    formValues: null,
    canSwitchToOrpheus: false,
    notice: '',
    quotaUsed: 0,
    quotaLimit: null,
  },
)
const open = defineModel<boolean>({ default: false })
const emit = defineEmits<{
  saved: [rule: AlertRule | null]
  close: []
  /** Carries what the form holds so far, as the setup's first message. */
  'open-orpheus': [brief: string]
}>()
const { t } = useI18n()
const toast = useToast()
const router = useRouter()
const wizard = useAlertWizard({
  ...(props.rule ? { seed: props.rule } : {}),
  ...(props.formValues ? { formValues: props.formValues } : {}),
  readOnly: props.readOnly,
  workspaceId: () => props.workspaceId,
  notificationTypeId: () => props.notificationTypeId,
  channelAvailability: () => props.channels,
})
const summary = useAlertSummary(wizard.draft)
const attemptedSteps = ref<AlertStepId[]>([])

const stepMeta = computed(() =>
  wizard.steps.map((id) => ({
    id,
    title: t(`alerts.wizard.step.${id}.title`),
    description: t(`alerts.wizard.step.${id}.description`),
  })),
)
const activeMeta = computed(
  () => stepMeta.value.find((step) => step.id === wizard.activeStep.value) ?? stepMeta.value[0],
)
const structuralReadOnly = computed(() => wizard.draft.value.readOnly || props.manageDisabled)
const editableSteps = computed<AlertStepId[]>(() => {
  if (props.manageDisabled) return []
  return wizard.draft.value.readOnly ? ['notify'] : ['monitor', 'filter', 'conditions', 'notify']
})
const stepNumber = computed(() => wizard.steps.indexOf(wizard.activeStep.value) + 1)
const isReview = computed(() => wizard.activeStep.value === 'review')
const isCloseAction = computed(
  () => props.manageDisabled || (wizard.draft.value.readOnly && !wizard.isDirty.value),
)
const quotaReached = computed(
  () => !props.rule && props.quotaLimit !== null && (props.quotaUsed ?? 0) >= props.quotaLimit,
)
const quotaError = ref(false)
const primaryLabel = computed(() => {
  if (!isReview.value) return t('alerts.actions.next')
  if (isCloseAction.value) return t('common.close')
  return t(props.rule ? 'alerts.actions.update' : 'alerts.actions.create')
})

function switchToOrpheus(): void {
  emit('open-orpheus', alertOrpheusBrief(summary.sections.value))
}

function visibleError(step: AlertStepId) {
  return attemptedSteps.value.includes(step) ? wizard.stepErrors.value[step] : null
}

function visibleErrors(step: AlertStepId): readonly AlertValidationKey[] {
  return attemptedSteps.value.includes(step) ? wizard.stepErrorKeys.value[step] : []
}

function markAttempted(step: AlertStepId): void {
  if (!attemptedSteps.value.includes(step)) attemptedSteps.value = [...attemptedSteps.value, step]
}

function nextStep(): void {
  if (!wizard.next()) markAttempted(wizard.activeStep.value)
}

async function requestOpen(value: boolean): Promise<void> {
  if (value) return
  if (await wizard.requestClose()) {
    open.value = false
    emit('close')
  }
}

async function save(): Promise<void> {
  if (quotaReached.value) {
    quotaError.value = true
    return
  }
  try {
    const result = await wizard.save()
    if (!result.ok) {
      const status =
        result.error && typeof result.error === 'object' && 'status' in result.error
          ? result.error.status
          : null
      if (status === 403) {
        quotaError.value = true
        return
      }
      if (wizard.activeStep.value === 'conditions') {
        markAttempted('conditions')
        return
      }
      toast.error(t(props.rule ? 'alerts.toast.updateFailed' : 'alerts.toast.createFailed'))
      return
    }
    toast.success(
      t(props.rule ? 'alerts.toast.updated' : 'alerts.toast.created', {
        name: wizard.draft.value.name,
      }),
    )
    emit('saved', props.rule)
    open.value = false
  } catch (cause) {
    if (cause instanceof AlertValidationError) {
      markAttempted(wizard.activeStep.value)
      return
    }
    toast.error(t(props.rule ? 'alerts.toast.updateFailed' : 'alerts.toast.createFailed'))
  }
}

async function primaryAction(): Promise<void> {
  if (!isReview.value) return nextStep()
  if (isCloseAction.value) return requestOpen(false)
  await save()
}
</script>

<template>
  <div class="alert-wizard-host w-0 min-w-0">
    <Modal
      :model-value="open"
      size="2xl"
      :title="
        t(
          manageDisabled
            ? 'alerts.wizard.viewTitle'
            : rule
              ? 'alerts.wizard.updateTitle'
              : 'alerts.wizard.createTitle',
        )
      "
      :close-label="t('common.close')"
      @update:model-value="requestOpen"
    >
      <div
        class="alert-wizard grid grid-cols-[minmax(0,8fr)_minmax(15rem,4fr)] gap-lg max-[56rem]:grid-cols-1"
      >
        <section
          class="alert-wizard__main grid min-w-0 content-start gap-md"
          :data-alert-step="wizard.activeStep.value"
        >
          <p v-if="notice" class="m-0 text-sm text-muted" data-testid="alert-wizard-notice">
            {{ notice }}
          </p>
          <header class="grid gap-2xs">
            <div class="flex flex-wrap items-center gap-3">
              <h2 class="m-0 text-[1.25rem] font-semibold">{{ activeMeta?.title }}</h2>
              <AlertWizardOrpheusSwitch
                v-if="canSwitchToOrpheus && !rule && !quotaReached"
                @switch="switchToOrpheus"
              />
            </div>
            <p class="m-0 text-muted">{{ activeMeta?.description }}</p>
          </header>
          <Alert
            v-if="structuralReadOnly"
            tone="warning"
            :title="t(manageDisabled ? 'alerts.rbac.readOnly' : 'alerts.wizard.readOnlyTitle')"
          >
            {{ t(manageDisabled ? 'alerts.rbac.readOnly' : 'alerts.wizard.readOnlyBanner') }}
          </Alert>
          <Alert v-if="quotaError || quotaReached" tone="warning" :title="t('alerts.quota.title')">
            <div class="grid gap-sm">
              <span>{{ t('alerts.quota.body', { used: quotaUsed, limit: quotaLimit }) }}</span>
              <div class="flex flex-wrap gap-sm">
                <Button variant="outline" size="sm" @click="router.push({ name: 'ComparePlans' })">
                  {{ t('alerts.quota.upgrade') }}
                </Button>
                <Button variant="soft" size="sm" @click="router.push({ name: 'Alerts' })">
                  {{ t('alerts.actions.manage') }}
                </Button>
              </div>
            </div>
          </Alert>
          <div
            class="alert-wizard__step grid min-w-0 content-start gap-md h-[55vh] overflow-x-hidden overflow-y-auto max-sm:h-[52vh]"
          >
            <StepMonitor
              v-if="wizard.activeStep.value === 'monitor'"
              :draft="wizard.draft.value"
              :levels="wizard.levels.value"
              :fields-loading="wizard.fieldsLoading.value"
              :error="visibleError('monitor')"
              :errors="visibleErrors('monitor')"
              :read-only="structuralReadOnly"
              @update:name="wizard.updateDraft({ name: $event })"
              @update:level="wizard.setLevel"
              @update:group-by="wizard.setGroupBy"
            />
            <StepFilter
              v-else-if="wizard.activeStep.value === 'filter'"
              :filters="wizard.draft.value.filters"
              :columns="[
                ...(wizard.draft.value.level?.tableDimensions ?? []),
                ...(wizard.draft.value.level?.tableMetrics ?? []),
              ]"
              :dropdownables="wizard.filterDropdownables.value"
              :on-load-dropdown-values="wizard.loadFilterDropdownValues"
              :read-only="structuralReadOnly"
              @update:filters="wizard.updateDraft({ filters: $event })"
            />
            <StepConditions
              v-else-if="wizard.activeStep.value === 'conditions'"
              :draft="wizard.draft.value"
              :fields-loading="wizard.fieldsLoading.value"
              :error="visibleError('conditions')"
              :read-only="structuralReadOnly"
              @update:window="wizard.setWindow"
              @update:custom-days="wizard.updateDraft({ customWindowDays: $event })"
              @update:metric-fields="wizard.setMetricFields"
              @update:condition="wizard.updateCondition"
              @update:condition-join="wizard.setConditionJoin"
            />
            <StepNotify
              v-else-if="wizard.activeStep.value === 'notify'"
              :draft="wizard.draft.value"
              :availability="channels"
              :error="visibleError('notify')"
              :disabled="manageDisabled"
              @update:channels="wizard.updateDraft({ channels: $event })"
              @update:emails="wizard.updateDraft({ recipientEmails: $event })"
              @update:numbers="wizard.updateDraft({ whatsappNumbers: $event })"
            />
            <StepReview
              v-else
              :sections="summary.sections.value"
              :editable-steps="editableSteps"
              @edit="wizard.goTo"
            />
          </div>
        </section>
        <aside
          class="alert-wizard__summary h-[55vh] overflow-x-hidden overflow-y-auto rounded-lg border border-border bg-surface-sunken p-lg max-[56rem]:hidden"
        >
          <AlertSummaryPanel
            :sections="summary.sections.value"
            :active-step="wizard.activeStep.value"
          />
          <p
            v-if="quotaLimit !== null"
            class="m-0 text-sm text-muted"
            data-testid="alert-wizard-quota"
          >
            {{ t('alerts.quota.used', { used: quotaUsed, limit: quotaLimit }) }}
          </p>
        </aside>
      </div>

      <template #footer>
        <div
          class="alert-wizard__footer flex w-full items-center justify-between gap-sm max-sm:flex-col max-sm:items-stretch"
        >
          <div>
            <small class="text-muted">{{
              t('alerts.wizard.stepOf', { current: stepNumber, total: wizard.steps.length })
            }}</small>
            <div class="alert-wizard__progress flex items-center gap-sm" aria-hidden="true">
              <span
                v-for="(step, index) in wizard.steps"
                :key="step"
                class="h-2 rounded-full"
                :class="index + 1 === stepNumber ? 'is-active w-8 bg-info' : 'w-2 bg-border-strong'"
              />
            </div>
          </div>
          <div
            class="alert-wizard__actions flex items-center gap-sm max-sm:flex-wrap max-sm:justify-end"
          >
            <Button v-if="stepNumber === 1" variant="soft" @click="requestOpen(false)">{{
              t('common.cancel')
            }}</Button>
            <Button v-else variant="soft" @click="wizard.back">{{
              t('alerts.actions.back')
            }}</Button>
            <Button
              :loading="wizard.saving.value"
              :disabled="manageDisabled && !isCloseAction"
              @click="primaryAction"
            >
              {{ primaryLabel }}
            </Button>
          </div>
        </div>
      </template>
    </Modal>
  </div>
</template>
